/**
 * section-identity.js — the boundary between a template section's TWO names.
 *
 * C3 gave adopted template sections a synthesized catalog identity while
 * deliberately leaving their source identity in place. Both are load-bearing
 * and neither can be removed:
 *
 *   SOURCE space   `templateSectionId`   `cs-footer-1110`
 *                  per-site filenames (`data/templates/<site>/<id>.json`),
 *                  `_page-map.json` entries, `~variant` suffixes, and
 *                  thumbnail basenames (`template-thumbnails/<site>/<id>.png`)
 *
 *   CATALOG space  `catalogKey`          `template-<site>-footer-nav-8101`
 *                  the shared library (`data/sections/<category>/<key>.json`),
 *                  fleet identity, and provenance resolution
 *
 * A published site's two names are identical, which is why nothing noticed the
 * boundary existed for 200 templates. An ADOPTED site's are not, and every
 * consumer that reads one space and looks up in the other has to translate.
 *
 * ## Why this module exists rather than a fix at the call site
 *
 * `build-template-bundles.js` crossed this boundary by assuming a library
 * filename's base equals a page-map id. For lawngoblins.com — the first site
 * ever ingested with identity adoption on — all 14 page-map ids missed, the
 * site resolved zero sections, and it silently produced NO bundle at all. The
 * defect was not the lookup; it was that the boundary was implicit, so nothing
 * said which consumers cross it or proved that they cross it correctly.
 *
 * So: one index, built from records that already carry BOTH names, and one
 * gate (`tests/page-map-identity-gate.js`) that asserts every page-map id in
 * the corpus resolves. Derived, never stored — there is no alias file to fall
 * out of sync with the data it describes.
 *
 * Pure and dependency-free on purpose: the parsers, the bundle builder, the
 * gate and the browser-side consumers must all be able to import it.
 */

/** `hero-2410~contact` → `hero-2410`. A variant is a page-scoped copy of a section. */
export function stripVariantSuffix(sectionName) {
  return String(sectionName ?? '')
    .trim()
    .split('~')[0];
}

/** `hero-2410~contact` → `contact`, or null when there is no variant. */
export function variantSuffix(sectionName) {
  const parts = String(sectionName ?? '')
    .trim()
    .split('~');
  return parts.length > 1 ? parts.slice(1).join('~') : null;
}

/** Join a base identity and page suffix without inheriting a stale suffix. */
export function appendVariantSuffix(sectionName, suffix) {
  const base = stripVariantSuffix(sectionName);
  const cleanSuffix = String(suffix ?? '')
    .trim()
    .replace(/^~+/, '');
  return base && cleanSuffix ? `${base}~${cleanSuffix}` : base;
}

/**
 * Index a site's sections under BOTH of their names.
 *
 * @param {Array<{sourceId?: string, canonicalId?: string, value: *}>} records
 *   `canonicalId` is the catalogKey with its `template-<site>-` prefix removed
 *   — i.e. what the library filename actually says. `sourceId` is
 *   `templateSectionId`. For a published site the two are equal and this
 *   degenerates to the single-key map it replaces.
 * @returns {{byKey: Map<string, *>, conflicts: Array<{key: string}>}}
 *
 * CONFLICTS ARE REPORTED, NOT RESOLVED. Indexing two namespaces into one map
 * can only be trusted while they do not overlap: if one section's source id
 * equals a different section's canonical id, a lookup silently returns the
 * wrong section, which is worse than the miss this module exists to fix. First
 * writer wins so the result stays deterministic, and the collision is handed
 * back for the gate to fail on.
 */
export function buildSectionIdentityIndex(records) {
  const byKey = new Map();
  const owner = new Map();
  const conflicts = [];

  // Sorted so the winner of any collision does not depend on directory order.
  const ordered = [...(records || [])].sort((a, b) =>
    String(a?.canonicalId ?? '').localeCompare(String(b?.canonicalId ?? ''))
  );

  for (const record of ordered) {
    const canonicalId = typeof record?.canonicalId === 'string' ? record.canonicalId.trim() : '';
    const identity = canonicalId || record?.sourceId || null;
    // A VARIANT record (`hero-2410~contact`) shares its base section's
    // sourceId, so aliasing every variant to it would report one collision per
    // page — 40 on a blog-heavy site. The base record already supplies that
    // alias, and `resolveSectionRef` strips the suffix before looking up, so a
    // variant only ever needs its own literal key.
    const keys = variantSuffix(canonicalId) ? [canonicalId] : [canonicalId, record?.sourceId];

    for (const key of keys) {
      const name = typeof key === 'string' ? key.trim() : '';
      if (!name) continue;
      if (byKey.has(name)) {
        // Two records may share a key only when they are the same section —
        // a base and its own variants. Different sections colliding is the
        // case that silently returns the wrong one.
        if (stripVariantSuffix(owner.get(name)) !== stripVariantSuffix(identity)) {
          conflicts.push({ key: name, kept: owner.get(name), dropped: identity });
        }
        continue;
      }
      byKey.set(name, record.value);
      owner.set(name, identity);
    }
  }

  return { byKey, conflicts };
}

/**
 * Resolve one `_page-map.json` id (with or without a `~variant` suffix) to
 * whatever the index was built over, or null.
 */
export function resolveSectionRef(index, sectionName) {
  const base = stripVariantSuffix(sectionName);
  if (!base) return null;
  const map = index instanceof Map ? index : index?.byKey;
  if (!map) return null;
  return map.get(base) ?? null;
}

/** The catalogKey with its site prefix removed, or null when it has none. */
export function canonicalIdFromCatalogKey(catalogKey, siteName) {
  const key = String(catalogKey ?? '');
  const prefix = `template-${siteName}-`;
  return key.startsWith(prefix) ? key.slice(prefix.length) : null;
}

/**
 * The source template a section was loaded FROM, read off its `provenancePath`.
 *
 * `data/templates/<site>/<id>.json` → `<site>`; anything else → `''`. A
 * `data/sections/...` path is a shared catalog stitch with no single template
 * origin, and saying so is the correct answer — not a fallback.
 *
 * This reads the path's OWN site segment and therefore does NOT cross the
 * source/catalog boundary this module exists to police: no `templateSectionId`
 * is resolved to a `catalogKey` or back. Provenance that does need that
 * crossing goes through `data/section-provenance.json`, whose entries carry
 * `templateSiteName` explicitly.
 *
 * @param {string} provenancePath
 * @returns {string} the site domain, or '' when the path names no template
 */
export function templateSiteNameFromProvenancePath(provenancePath) {
  const path = String(provenancePath ?? '').trim();
  if (!path.startsWith('data/templates/')) return '';
  const site = path.slice('data/templates/'.length).split('/')[0] || '';
  // A path with no id segment after the site names a directory, not a section.
  return path.slice('data/templates/'.length).includes('/') ? site : '';
}
